import type { ForecastImportResult, ForecastOverview, ForecastProject, Me, WorkdaySyncRun } from '@ft/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loading, useLoad, useToast } from '../components/ui';
import { SavedViews } from '../components/SavedViews';
import { useI18n, type T } from '../i18n';
import { canEdit as canEditArea, getAreaName } from '../lib/area';
import { upload } from '../lib/download';
import { dtfmt, eur, pct, tk } from '../lib/format';
import { api } from '../lib/http';
import { Chip, CollapseAll, Panel } from '../lib/collapse';
import { ForecastExplain } from '../components/ForecastExplain';

/**
 * Forecast: erwarteter Umsatz je Account, Projekt und Monat aus den Firmen-Forecast-Dateien (iFC, SharePoint ProjectDelivery),
 * daneben das Ist aus den Rechnungen. Accounts lassen sich auf- und zuklappen und im Diagramm ein- und ausblenden.
 */
type Account = { id: string; name: string; logoRev: string | null; color: string; projects: ForecastProject[]; forecast: number[]; actual: number[]; forecastTotal: number; actualTotal: number; expectedTotal: number; target: number; uncovered: number };

const store = {
  get: (k: string): string[] => { try { return JSON.parse(localStorage.getItem(k) ?? '[]') as string[]; } catch { return []; } },
  set: (k: string, v: string[]) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* privater Modus */ } },
};
const COLLAPSED = 'ft.forecast.collapsed', HIDDEN = 'ft.forecast.hidden', CHART = 'ft.forecast.chart', UNIT = 'ft.forecast.unit', COLS = 'ft.forecast.cols';
/** Spalten der Tabelle: Monate, Quartale oder nur Summen */
type Cols = 'm' | 'q' | 's';
type Period = { key: string; label: string; idx: number[]; now: boolean };
function periodsOf(cols: Cols, months: string[], cm: number): Period[] {
  if (cols === 's') return [];
  if (cols === 'q') return [0, 1, 2, 3].map((q) => { const idx = [q * 3, q * 3 + 1, q * 3 + 2]; return { key: `q${q}`, label: `Q${q + 1}`, idx, now: idx.includes(cm) }; });
  return months.map((label, i) => ({ key: `m${i}`, label, idx: [i], now: i === cm }));
}
const sumAt = (arr: readonly (number | null)[], idx: number[]) => idx.reduce((s, i) => s + (arr[i] ?? 0), 0);
/** Ist nur bis zum laufenden Monat */
const istAt = (arr: readonly (number | null)[], idx: number[], cm: number) => idx.filter((i) => i <= cm).reduce((s, i) => s + (arr[i] ?? 0), 0);
const KPI_IDS = ['fc.kpis', 'fc.chart', 'fc.table'];
const mio = (c: number) => (Math.abs(c) >= 100_000_000 ? `${(c / 100_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 })} Mio` : `${Math.round(c / 100_000)} T`);

export function AccountMark({ id, name, logoRev, color, size = 28 }: { id: string; name: string; logoRev: string | null; color: string; size?: number }) {
  const [broken, setBroken] = useState(false);
  if (logoRev && !broken) return <span className="accmark logo" style={{ width: size, height: size }}><img src={`/api/customers/${id}/logo?v=${logoRev}`} alt="" onError={() => setBroken(true)} /></span>;
  const mono = name.replace(/\b(AG|GmbH|SE|Group)\b/g, '').trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 3).toUpperCase() || name.slice(0, 2).toUpperCase();
  const text = name.length <= 4 ? name.toUpperCase() : mono;
  return <span className="accmark" style={{ width: size, height: size, background: color, fontSize: text.length > 2 ? size * 0.3 : size * 0.38 }} aria-hidden="true">{text}</span>;
}

export function ForecastPage({ me }: { me: Me }) {
  const { t, lang } = useI18n();
  const [sp, setSp] = useSearchParams();
  const thisYear = new Date().getFullYear();
  const year = Number(sp.get('jahr') ?? thisYear);
  const all = sp.get('alle') === '1';
  const { data, error, reload } = useLoad<ForecastOverview>(`/api/forecast?year=${year}`);
  const edit = canEditArea(me);
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'de-DE', { month: 'short' }).format(new Date(Date.UTC(2026, i, 1)))), [lang]);
  const set = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  const [collapsed, setCollapsed] = useState<string[]>(() => store.get(COLLAPSED));
  const [hidden, setHidden] = useState<string[]>(() => store.get(HIDDEN));
  const [toast, say] = useToast();
  const [chartMode, setChartMode] = useState<'2d' | '3d'>(() => (store.get(CHART)[0] === '2d' ? '2d' : '3d'));
  const pickMode = (v: '2d' | '3d') => { setChartMode(v); store.set(CHART, [v]); };
  const [unit, setUnit] = useState<'k' | 'eur'>(() => (store.get(UNIT)[0] === 'eur' ? 'eur' : 'k'));
  const pickUnit = (v: 'k' | 'eur') => { setUnit(v); store.set(UNIT, [v]); };
  const mv = (c: number) => (unit === 'k' ? tk(c) : eur(c));
  const [cols, setCols] = useState<Cols>(() => { const v = store.get(COLS)[0]; return v === 'q' || v === 's' ? v : 'm'; });
  const pickCols = (v: Cols) => { setCols(v); store.set(COLS, [v]); };
  const toggle = (list: string[], setList: (v: string[]) => void, key: string, id: string) => { const n = list.includes(id) ? list.filter((x) => x !== id) : [...list, id]; setList(n); store.set(key, n); };

  // Während eine Anforderung offen ist, jede Minute nachsehen
  const pendingId = data?.sync.pending?.id;
  useEffect(() => {
    if (!pendingId) return;
    const h = setInterval(reload, 60_000);
    return () => clearInterval(h);
  }, [pendingId, reload]);

  // Accounts: Farbe folgt dem Account (feste Reihenfolge nach Name), nicht dem Rang
  const accounts = useMemo<Account[]>(() => {
    if (!data) return [];
    return data.customers.map((c, i) => {
      const projects = data.projects.filter((p) => p.customer.id === c.id && (all || p.forecastTotalCents || p.actualTotalCents))
        .sort((a, b) => b.forecastTotalCents - a.forecastTotalCents || a.name.localeCompare(b.name));
      const sum = (f: (p: ForecastProject) => number) => projects.reduce((s, p) => s + f(p), 0);
      return {
        id: c.id, name: c.name, logoRev: c.logoRev, color: `var(--acc-${(i % 8) + 1})`, projects,
        forecast: Array.from({ length: 12 }, (_, m) => sum((p) => p.forecastCents[m] ?? 0)),
        actual: Array.from({ length: 12 }, (_, m) => sum((p) => p.actualCents[m]!)),
        forecastTotal: sum((p) => p.forecastTotalCents), actualTotal: sum((p) => p.actualTotalCents), expectedTotal: sum((p) => p.expectedTotalCents), target: sum((p) => p.targetCents ?? 0), uncovered: sum((p) => p.uncoveredCents),
      };
    }).filter((a) => a.projects.length).sort((a, b) => b.forecastTotal - a.forecastTotal);
  }, [data, all]);

  const cm = data?.currentMonth ?? 0;
  const tot = data?.totals;
  const allCollapsed = accounts.length > 0 && accounts.every((a) => collapsed.includes(a.id));
  const periods = periodsOf(cols, months, cm);
  const shownFc = accounts.filter((a) => !hidden.includes(a.id));
  const monthFc = months.map((_, m) => shownFc.reduce((s, a) => s + a.forecast[m]!, 0));
  const monthIst = months.map((_, m) => shownFc.reduce((s, a) => s + a.actual[m]!, 0));
  const fcMonths = monthFc.filter((v) => v > 0);

  return (
    <section className="page full fcpage">
      <div className="pagehead">
        <div><div className="crumb">{t('crumb.root', { area: getAreaName() })}</div><h1>{t('fc.title')}</h1></div>
        <span className="spacer" />
        <CollapseAll ids={KPI_IDS} />
        <SavedViews />
        <div className="fld">
          <label htmlFor="fc-y">{t('pz.year')}</label>
          <select id="fc-y" className="yearsel" value={year} onChange={(e) => set('jahr', e.target.value === String(thisYear) ? '' : e.target.value)}>
            {[thisYear - 1, thisYear, thisYear + 1].map((y) => <option key={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {!data || !tot ? <Loading error={error} /> : (
        <>
          <SyncBox data={data} canEdit={edit} onChange={reload} t={t} />
          {year === thisYear && <ForecastExplain data={data} t={t} />}

          <Panel id="fc.kpis" title={t('fc.kpisTitle', { y: year })} className="fckpibox"
            summary={<>
              <Chip>{t('fc.kpi.forecast', { y: year })} <b>{eur(tot.forecastTotalCents)}</b></Chip>
              <Chip>{t('fc.kpi.actual')} <b>{eur(tot.actualTotalCents)}</b></Chip>
              {tot.uncoveredCents > 0 && <Chip tone="crit">{t('fc.kpi.uncovered')} <b>{eur(tot.uncoveredCents)}</b></Chip>}
            </>}>
          <div className="fckpis">
            <div className="fckpi">
              <div className="lbl">{t('fc.kpi.forecast', { y: year })}</div>
              <div className="val num">{eur(tot.forecastTotalCents)}</div>
              {tot.targetCents > 0 && <div className="fcmeter" title={pct(tot.forecastTotalCents / tot.targetCents)}><i style={{ width: `${Math.min(100, (tot.forecastTotalCents / tot.targetCents) * 100)}%` }} /></div>}
              <div className="sub">{tot.targetCents ? t('fc.kpi.ofTarget', { p: pct(tot.forecastTotalCents / tot.targetCents), target: eur(tot.targetCents) }) : t('fc.kpi.fromFiles')}</div>
            </div>
            <div className="fckpi">
              <div className="lbl">{t('fc.kpi.actual')}</div>
              <div className="val num">{eur(tot.actualTotalCents)}</div>
              {tot.forecastTotalCents > 0 && <div className="fcmeter"><i style={{ width: `${Math.min(100, (tot.actualTotalCents / tot.forecastTotalCents) * 100)}%` }} /></div>}
              <div className="sub">{tot.forecastTotalCents ? t('fc.kpi.actualOf', { p: pct(tot.actualTotalCents / tot.forecastTotalCents) }) : t('fc.kpi.actualSub')}</div>
            </div>
            <div className="fckpi">
              <div className="lbl">{t('fc.kpi.expected')}</div>
              <div className="val num">{eur(tot.expectedTotalCents)}</div>
              <div className={`sub${tot.targetCents && tot.expectedTotalCents < tot.targetCents ? ' neg' : ''}`}>{tot.targetCents ? t('fc.kpi.gap', { v: eur(tot.expectedTotalCents - tot.targetCents) }) : t('fc.kpi.expectedSub')}</div>
            </div>
            <div className="fckpi">
              <div className="lbl">{t('fc.kpi.uncovered')}</div>
              <div className={`val num${tot.uncoveredCents ? ' crit' : ''}`}>{eur(tot.uncoveredCents)}</div>
              <div className="sub">{t('fc.kpi.uncoveredSub', { rest: eur(tot.forecastRestCents), open: eur(tot.openOrderCents) })}</div>
            </div>
            <div className="fckpi accs">
              <div className="lbl">{t('fc.kpi.accounts')}</div>
              <ul>{accounts.map((a) => (
                <li key={a.id}><AccountMark id={a.id} name={a.name} logoRev={a.logoRev} color={a.color} size={20} /><span className="n">{a.name}</span><span className="num">{eur(a.forecastTotal)}</span><span className="muted small num">{tot.forecastTotalCents ? pct(a.forecastTotal / tot.forecastTotalCents) : ''}</span></li>
              ))}</ul>
            </div>
          </div>
          </Panel>

          <Panel id="fc.chart" title={t('fc.chart', { y: year })} hint={t('fc.chartHint')}
            summary={<>
              {fcMonths.length > 0 && <Chip>{t('fc.sum.avg')} <b>{eur(Math.round(fcMonths.reduce((a, b) => a + b, 0) / fcMonths.length))}</b></Chip>}
              {cm >= 0 && cm < 12 && <Chip tone="info">{months[cm]} <b>{eur(monthFc[cm]!)}</b> · {t('fc.legend.actual')} {eur(monthIst[cm]!)}</Chip>}
            </>}
            actions={<div className="seg" role="group" aria-label={t('fc.chartView')}>
              <button aria-pressed={chartMode === '2d'} onClick={() => pickMode('2d')}>2D</button>
              <button aria-pressed={chartMode === '3d'} onClick={() => pickMode('3d')}>3D</button>
            </div>}>
            <div className="fclegend" role="group" aria-label={t('fc.legendLabel')}>
              {accounts.map((a) => (
                <button key={a.id} className="fcchip" aria-pressed={!hidden.includes(a.id)} onClick={() => toggle(hidden, setHidden, HIDDEN, a.id)}>
                  <i style={{ background: a.color }} />{a.name}
                </button>
              ))}
              <span className="fcchip static"><i className="ist" />{t('fc.legend.actual')}</span>
            </div>
            <StackChart months={months} accounts={shownFc} current={cm} t={t} mode={chartMode} />
          </Panel>

          <Panel id="fc.table" title={t('fc.accounts', { n: accounts.length, p: accounts.reduce((s, a) => s + a.projects.length, 0) })} hint={t('fc.tableHint')}
            summary={<><Chip>{t('fc.col.forecast')} <b>{eur(tot.forecastTotalCents)}</b></Chip><Chip>{t('fc.col.actual')} <b>{eur(tot.actualTotalCents)}</b></Chip></>}
            actions={<>
              <div className="seg" role="group" aria-label={t('fc.cols')}>
                <button aria-pressed={cols === 'm'} onClick={() => pickCols('m')}>{t('fc.cols.m')}</button>
                <button aria-pressed={cols === 'q'} onClick={() => pickCols('q')}>{t('fc.cols.q')}</button>
                <button aria-pressed={cols === 's'} onClick={() => pickCols('s')}>{t('fc.cols.s')}</button>
              </div>
              <button className="btn ghost" onClick={() => { const n = allCollapsed ? [] : accounts.map((a) => a.id); setCollapsed(n); store.set(COLLAPSED, n); }}>{allCollapsed ? t('fc.expandAll') : t('fc.collapseAll')}</button>
              <label className="small"><input type="checkbox" checked={all} onChange={(e) => set('alle', e.target.checked ? '1' : '')} /> {t('fc.showAll')}</label>
              <div className="seg" role="group" aria-label={t('fc.unit')}>
                <button aria-pressed={unit === 'k'} onClick={() => pickUnit('k')} title={t('fc.unitK')}>T€</button>
                <button aria-pressed={unit === 'eur'} onClick={() => pickUnit('eur')} title={t('fc.unitEur')}>€</button>
              </div>
            </>}>
            <div className="tablewrap">
              {!accounts.length ? <div className="empty">{t('fc.empty')}</div> : (
                <table className={`fctable${unit === 'k' ? ' k' : ''}`}>
                  <thead><tr>
                    <th>{t('fc.col.account')}</th>
                    <th className="fc-file">{t('fc.col.file')}</th>
                    {periods.map((pd) => <th key={pd.key} className={`r${pd.now ? ' fc-now' : ''}`}>{pd.label}</th>)}
                    <th className="r fc-col">{t('fc.col.forecast')}</th><th className="r">{t('fc.col.actual')}</th><th className="r">{t('fc.col.expected')}</th><th className="r" title={t('fc.col.uncoveredHelp')}>{t('fc.col.uncovered')}</th><th className="r">{t('fc.col.target')}</th>
                  </tr></thead>
                  {accounts.map((a) => {
                    const open = !collapsed.includes(a.id);
                    return (
                      <tbody key={a.id} className="fcgroup" style={{ ['--acc' as string]: a.color }}>
                        <tr className="fcacc">
                          <td>
                            <button className="fcacc-btn" aria-expanded={open} onClick={() => toggle(collapsed, setCollapsed, COLLAPSED, a.id)}>
                              <svg className="chev" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 4l4 4-4 4" /></svg>
                              <AccountMark id={a.id} name={a.name} logoRev={a.logoRev} color={a.color} />
                              <span><b>{a.name}</b><span className="muted small"> · {a.projects.length === 1 ? t('fc.oneProject') : t('fc.nProjects', { n: a.projects.length })}</span></span>
                            </button>
                            {me.role === 'admin' && <LogoButton id={a.id} has={!!a.logoRev} onDone={reload} say={say} t={t} />}
                          </td>
                          <td className="fc-file" />
                          {periods.map((pd) => { const v = sumAt(a.forecast, pd.idx), ist = istAt(a.actual, pd.idx, cm); return (
                            <td key={pd.key} className={`r num${pd.now ? ' fc-now' : ''}`}>{v ? mv(v) : '–'}{ist ? <div className="fc-ist">{mv(ist)}</div> : null}</td>
                          ); })}
                          <td className="r num fc-col"><b>{eur(a.forecastTotal)}</b></td>
                          <td className="r num">{eur(a.actualTotal)}</td>
                          <td className="r num">{eur(a.expectedTotal)}</td>
                          <td className={`r num${a.uncovered ? ' fc-unc' : ''}`}>{a.uncovered ? eur(a.uncovered) : '–'}</td>
                          <td className="r num">{a.target ? <>{eur(a.target)}<div className="muted small">{pct(a.expectedTotal / a.target)}</div></> : '–'}</td>
                        </tr>
                        {open && a.projects.map((p) => <Row key={p.id} p={p} current={cm} periods={periods} t={t} mv={mv} admin={me.role === 'admin'} />)}
                      </tbody>
                    );
                  })}
                  <tfoot>
                    <tr>
                      <td>{t('fc.sumForecast')}</td>
                      <td className="fc-file" />
                      {periods.map((pd) => { const v = sumAt(tot.forecastCents, pd.idx); return <td key={pd.key} className="r num">{v ? mv(v) : '–'}</td>; })}
                      <td className="r num fc-col">{eur(tot.forecastTotalCents)}</td><td className="r num">{eur(tot.actualTotalCents)}</td><td className="r num">{eur(tot.expectedTotalCents)}</td><td className={`r num${tot.uncoveredCents ? ' fc-unc' : ''}`}>{tot.uncoveredCents ? eur(tot.uncoveredCents) : '–'}</td><td className="r num">{tot.targetCents ? eur(tot.targetCents) : '–'}</td>
                    </tr>
                    <tr className="fc-actual">
                      <td>{t('fc.sumActual')}</td>
                      <td className="fc-file" />
                      {periods.map((pd) => { const v = istAt(tot.actualCents, pd.idx, cm); return <td key={pd.key} className="r num">{v ? mv(v) : ''}</td>; })}
                      <td colSpan={5} />
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </Panel>
          <p className="muted small" style={{ margin: 0 }}>{t('fc.help', { y: data.importYear })}</p>
          {toast}
        </>
      )}
    </section>
  );
}

function Row({ p, current, periods, t, mv, admin }: { p: ForecastProject; current: number; periods: Period[]; t: T; mv: (c: number) => string; admin: boolean }) {
  const f = p.forecastFile;
  const tip = f ? [f.name ?? f.url, f.modified ? t('fc.file.modified', { d: dtfmt(f.modified) }) : '', t(f.source === 'manual' ? 'fc.file.manual' : 'fc.file.auto')].filter(Boolean).join('\n') : '';
  return (
    <tr className="fcproj">
      <td>
        <div className="fcproj-name" title={p.name}>{p.name}{p.workdayProjectId && <span className="muted small mono"> {p.workdayProjectId}</span>}</div>
        {!p.workdayProjectId && <div className="small fc-warn">{t('fc.noWorkdayId')}</div>}
      </td>
      <td className="fc-file">
        {f ? <a className="fcfile" href={f.url} target="_blank" rel="noreferrer" title={tip} aria-label={t('fc.file.open', { n: p.name })}>
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 1.5h5l3 3v10H4z M9 1.5v3h3" /></svg>iFC<span className="ext" aria-hidden="true">↗</span></a>
          : admin ? <a className="fcnofile" href="/verwaltung/projekte" title={t('fc.file.missingHelp')}>{t('fc.file.missing')}</a>
          : <span className="fcnofile" title={t('fc.file.missingHelp')}>{t('fc.file.missing')}</span>}
      </td>
      {periods.map((pd) => {
        const has = pd.idx.some((i) => p.forecastCents[i] !== null);
        const v = sumAt(p.forecastCents, pd.idx);
        const a = istAt(p.actualCents, pd.idx, current);
        const open = pd.idx.some((i) => p.forecastCents[i] && !p.contracted[i]);
        const tip = [`${t('fc.legend.forecast')}: ${has ? eur(v) : '–'}${open ? ` (${t('fc.notContracted')})` : ''}`, `${t('fc.legend.actual')}: ${eur(a)}`].join('\n');
        return (
          <td key={pd.key} className={`r num${pd.now ? ' fc-now' : ''}`} title={tip}>
            {v ? <span className={open ? 'fc-open' : undefined}>{mv(v)}</span> : <span className="muted">–</span>}
            {a !== 0 ? <div className="fc-ist">{mv(a)}</div> : null}
          </td>
        );
      })}
      <td className="r num fc-col">{eur(p.forecastTotalCents)}</td>
      <td className="r num">{eur(p.actualTotalCents)}</td>
      <td className="r num">{eur(p.expectedTotalCents)}</td>
      <td className={`r num${p.uncoveredCents ? ' fc-unc' : ''}`} title={`${eur(p.forecastRestCents)} − ${eur(p.openOrderCents)}`}>{p.uncoveredCents ? eur(p.uncoveredCents) : '–'}</td>
      <td className="r num">{p.targetCents !== null ? <>{eur(p.targetCents)}<div className="muted small">{p.targetCents ? pct(p.expectedTotalCents / p.targetCents) : ''}</div></> : '–'}</td>
    </tr>
  );
}

/** Gestapelte Säulen je Monat (Forecast je Account), dazu das Ist als Strich; Hover zeigt die Aufteilung. */
function StackChart({ months, accounts, current, t, mode }: { months: string[]; accounts: Account[]; current: number; t: T; mode: '2d' | '3d' }) {
  const [hover, setHover] = useState<number | null>(null);
  const fc = months.map((_, m) => accounts.reduce((s, a) => s + a.forecast[m]!, 0));
  const ist = months.map((_, m) => accounts.reduce((s, a) => s + a.actual[m]!, 0));
  const rawMax = Math.max(1, ...fc, ...ist);
  const step = [50_000_00, 100_000_00, 200_000_00, 250_000_00, 500_000_00, 1_000_000_00].find((s) => rawMax / s <= 5) ?? 2_000_000_00;
  const max = Math.ceil(rawMax / step) * step;
  const ticks = Array.from({ length: Math.round(max / step) + 1 }, (_, i) => i * step);
  return (
    <div className={`fcstack${mode === '3d' ? ' d3' : ''}`} onMouseLeave={() => setHover(null)}>
      <div className="fcaxis" aria-hidden="true">{ticks.slice().reverse().map((v) => <span key={v}>{mio(v)}</span>)}</div>
      <div className="fcplot">
        <div className="fcgrid" aria-hidden="true">{ticks.map((v) => <i key={v} style={{ bottom: `${(v / max) * 100}%` }} />)}</div>
        {months.map((mn, m) => (
          <div key={mn} className={`fcm${m > current ? ' future' : ''}${m === current ? ' now' : ''}${hover === m ? ' hover' : ''}`}
            onMouseEnter={() => setHover(m)} onFocus={() => setHover(m)} onBlur={() => setHover(null)} tabIndex={0}
            aria-label={`${mn}: ${t('fc.legend.forecast')} ${eur(fc[m]!)}, ${t('fc.legend.actual')} ${eur(ist[m]!)}`}>
            <div className="fcbar" style={{ ['--i' as string]: m }}>
              {accounts.filter((a) => a.forecast[m]).map((a, k, arr) => (
                <i key={a.id} style={{ height: `${(a.forecast[m]! / max) * 100}%`, background: a.color, ['--c' as string]: a.color }}>
                  {mode === '3d' && <><span className="sd" />{k === arr.length - 1 && <span className="tp" />}</>}
                </i>
              ))}
              {m <= current && ist[m]! > 0 && <b className="fcist" style={{ bottom: `${(ist[m]! / max) * 100}%` }} />}
            </div>
            <span className="ql">{mn}</span>
            {hover === m && (
              <div className={`fctip${m > 8 ? ' left' : ''}`} role="tooltip">
                <div className="h">{mn}</div>
                {accounts.filter((a) => a.forecast[m] || a.actual[m]).map((a) => <div key={a.id} className="r"><i style={{ background: a.color }} /><span>{a.name}</span><b className="num">{eur(a.forecast[m]!)}</b></div>)}
                <div className="r tot"><span>{t('fc.legend.forecast')}</span><b className="num">{eur(fc[m]!)}</b></div>
                {m <= current && <div className="r tot"><span>{t('fc.legend.actual')}</span><b className="num">{eur(ist[m]!)}</b></div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function LogoButton({ id, has, onDone, say, t }: { id: string; has: boolean; onDone: () => void; say: (m: string) => void; t: T }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  async function send(f: File | undefined) {
    if (!f) return;
    setBusy(true);
    try { await upload('PUT', `/api/admin/customers/${id}/logo`, f); onDone(); say(t('fc.logoSaved')); } catch (e) { say((e as Error).message); } finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  }
  return (
    <>
      <input ref={ref} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" hidden onChange={(e) => void send(e.target.files?.[0])} />
      <button className="fclogo-btn" onClick={() => ref.current?.click()} disabled={busy} title={has ? t('fc.logoChange') : t('fc.logoAdd')}>{has ? t('fc.logoChange') : t('fc.logoAdd')}</button>
    </>
  );
}

function SyncBox({ data, canEdit, onChange, t }: { data: ForecastOverview; canEdit: boolean; onChange: () => void; t: T }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const s = data.sync;
  const last = s.last;

  async function request() {
    setBusy(true); setMsg(null);
    try { await api('POST', '/api/forecast/sync/request'); onChange(); } catch (e) { setMsg((e as Error).message); } finally { setBusy(false); }
  }
  async function uploadFiles(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true); setMsg(null);
    const imported: { workdayProjectId: string; project: string; totalCents: number }[] = [];
    const unmatched: { file: string; reason: string }[] = [];
    for (const f of [...files]) {
      try {
        const r = await upload<ForecastImportResult>('POST', '/api/forecast/import', f);
        if (r.state === 'imported') imported.push({ workdayProjectId: r.workdayProjectId ?? '', project: r.project!.name, totalCents: r.totalCents });
        else unmatched.push({ file: f.name, reason: r.state === 'unmatched' ? t('fc.reason.unmatched', { id: r.workdayProjectId ?? '?' }) : t('fc.reason.empty', { y: data.importYear }) });
      } catch (e) { unmatched.push({ file: f.name, reason: (e as Error).message }); }
    }
    try { await api('POST', '/api/forecast/report', { status: 'done', files: files.length, imported, unmatched, manual: true }); } catch { /* Protokoll ist Nebensache */ }
    setMsg(t('fc.uploaded', { ok: imported.length, bad: unmatched.length }));
    if (fileRef.current) fileRef.current.value = '';
    setBusy(false);
    onChange();
  }

  return (
    <div className="box wdsync" aria-labelledby="fcsync-h">
      <div className="wdsync-row">
        <div className="wdsync-main">
          <div className="lbl" id="fcsync-h">{t('fc.sync.title')}</div>
          {last ? <RunLine run={last} t={t} /> : <div className="muted">{t('fc.sync.never')}</div>}
          {s.pending && <div className="wdsync-pending" role="status">{t('sync.pending', { by: s.pending.requestedBy ?? '–', at: dtfmt(s.pending.requestedAt!) })}</div>}
        </div>
        <span className="spacer" />
        {last && (last.newInvoices.length > 0 || last.unmatched.length > 0) && <button className="btn ghost" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{open ? t('sync.hide') : t('sync.details')}</button>}
        {canEdit && (
          <>
            <input ref={fileRef} type="file" accept=".xlsx" multiple hidden onChange={(e) => void uploadFiles(e.target.files)} />
            <button className="btn ghost" onClick={() => fileRef.current?.click()} disabled={busy}>{t('fc.upload')}</button>
            <button className="btn" onClick={request} disabled={busy || !!s.pending}>{s.pending ? t('sync.requested') : t('sync.now')}</button>
          </>
        )}
      </div>
      {msg && <p className="small" role="status">{msg}</p>}
      {open && last && (
        <div className="wdsync-detail">
          {last.newInvoices.length > 0 && (
            <table><tbody>{last.newInvoices.map((i) => <tr key={i.number}><td className="mono">{i.number}</td><td>{i.project}</td><td className="r num">{eur(i.amountCents)}</td></tr>)}</tbody></table>
          )}
          {last.unmatched.length > 0 && <ul className="small">{last.unmatched.map((u) => <li key={u.number}><span className="mono">{u.number}</span> · {u.projectText}</li>)}</ul>}
        </div>
      )}
    </div>
  );
}

function RunLine({ run, t }: { run: WorkdaySyncRun; t: T }) {
  const at = dtfmt(run.finishedAt ?? run.requestedAt ?? '');
  if (run.status === 'failed') return <div className="wdsync-fail">{t('fc.sync.failed', { at, msg: run.message ?? '' })}</div>;
  return (
    <div>
      {t('fc.sync.last', { at, by: run.by ?? '–' })} · <span className={run.newCount ? 'wdsync-new' : undefined}>{t('fc.sync.files', { n: run.newCount })}</span>
      {run.unmatchedCount > 0 && <> · <span className="wdsync-fail">{t('fc.sync.unmatched', { n: run.unmatchedCount })}</span></>}
    </div>
  );
}
